import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  LinearProgress,
} from '@mui/material';
import * as XLSX from 'xlsx';

// Types
interface FinancialQuestion {
  id: string;
  question: string;
  type: 'number';
  category: string;
  isPercent?: boolean;
  helpText?: string;
}

interface QuestionnaireResponse {
  [key: string]: string | number;
}

interface CustomExpense {
  title: string;
  amount: number;
}

interface Benchmark {
  label: string;
  value: number;
  benchmark: number;
  unit: string;
  status: 'good' | 'warning' | 'bad';
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const questions: FinancialQuestion[] = [
  {
    id: 'revenue',
    question: 'What is your total revenue?',
    type: 'number',
    category: 'Income',
    helpText: 'Enter your total sales/revenue for the period',
  },
  {
    id: 'adSpend',
    question: 'How much do you spend on advertising/marketing?',
    type: 'number',
    category: 'Expenses',
    helpText: 'Facebook, Google, TikTok ads, influencer payments, etc.',
  },
  {
    id: 'employeeSpend',
    question: 'How much do you spend on payroll/employees?',
    type: 'number',
    category: 'Expenses',
    helpText: 'Total payroll including benefits',
  },
  {
    id: 'salesExpenses',
    question: 'What do you spend on sales-related expenses?',
    type: 'number',
    category: 'Expenses',
    helpText: 'Gifting, affiliate fees, commissions, etc.',
  },
  {
    id: 'techExpenses',
    question: 'What are your tech/software expenses?',
    type: 'number',
    category: 'Expenses',
    helpText: 'Shopify, apps, tools, subscriptions',
  },
  {
    id: 'contractorExpenses',
    question: 'Other contractor/freelancer expenses?',
    type: 'number',
    category: 'Expenses',
    helpText: 'Designers, developers, consultants, etc.',
  },
  {
    id: 'shippingPercent',
    question: 'What % of revenue goes to shipping/fulfillment?',
    type: 'number',
    category: 'Operations',
    isPercent: true,
    helpText: '15-20% is typical for eCommerce',
  },
  {
    id: 'officeUtilities',
    question: 'Office + utilities expenses?',
    type: 'number',
    category: 'Operations',
    helpText: 'Rent, utilities, supplies, etc.',
  },
];

const categories = ['Income', 'Gross Margin', 'Expenses', 'Operations'];

// Industry benchmarks for eCommerce
const BENCHMARKS = {
  grossMargin: { good: 50, warning: 35 },
  adSpendPercent: { good: 20, warning: 35 },
  netMargin: { good: 15, warning: 5 },
  operatingExpensePercent: { good: 25, warning: 40 },
};

function GrossMarginStep({
  responses,
  handleResponse,
  onNext,
  timeframe
}: {
  responses: QuestionnaireResponse;
  handleResponse: (id: string, value: string | number) => void;
  onNext: () => void;
  timeframe: 'monthly' | 'annual';
}) {
  const [mode, setMode] = useState<'choose' | 'know' | 'calculate'>('choose');
  const revenue = Number(responses.revenue) || 0;
  const cogs = Number(responses.cogs) || 0;
  const calculatedMargin = revenue > 0 ? ((revenue - cogs) / revenue) * 100 : 0;

  const handleModeSelect = (selectedMode: 'know' | 'calculate') => {
    setMode(selectedMode);
    if (selectedMode === 'calculate') {
      // Clear any existing gross margin so it recalculates
      handleResponse('grossMargin', '');
    }
  };

  const handleCalculateNext = () => {
    if (revenue > 0 && cogs >= 0 && cogs < revenue) {
      handleResponse('grossMargin', calculatedMargin);
      onNext();
    }
  };

  const handleKnowNext = () => {
    if (responses.grossMargin) {
      onNext();
    }
  };

  if (mode === 'choose') {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Do you know your gross margin?
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
          Gross margin is the percentage of revenue left after subtracting the cost of goods sold (COGS).
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => handleModeSelect('know')}
            sx={{ 
              color: '#fff', 
              borderColor: '#fff',
              justifyContent: 'flex-start',
              py: 1.5,
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            ✓ Yes, I know my gross margin
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleModeSelect('calculate')}
            sx={{ 
              color: '#fff', 
              borderColor: '#fff',
              justifyContent: 'flex-start',
              py: 1.5,
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            🧮 Help me calculate it
          </Button>
        </Box>
      </Box>
    );
  }

  if (mode === 'calculate') {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Let's calculate your gross margin
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
          We already have your revenue (${revenue.toLocaleString()}). Now enter your Cost of Goods Sold (COGS).
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            What is your Cost of Goods Sold (COGS)?
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.7 }}>
            This includes: product cost, packaging, manufacturing, inbound freight — anything directly tied to making/buying your product.
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={responses.cogs || ''}
            onChange={(e) => {
              const val = parseFloat(e.target.value);
              if (!isNaN(val) && val >= 0) {
                handleResponse('cogs', val);
              } else if (e.target.value === '') {
                handleResponse('cogs', '');
              }
            }}
            InputProps={{
              startAdornment: <span style={{ marginRight: 4 }}>$</span>,
            }}
          />
        </Box>

        {cogs > 0 && revenue > 0 && (
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'rgba(255,255,255,0.1)', 
            borderRadius: 2,
            mb: 3
          }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Your calculated gross margin:
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {calculatedMargin.toFixed(1)}%
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              (${(revenue - cogs).toLocaleString()} gross profit from ${revenue.toLocaleString()} revenue)
            </Typography>
          </Box>
        )}

        {cogs >= revenue && cogs > 0 && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            COGS must be less than revenue
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button onClick={() => setMode('choose')} sx={{ color: '#fff' }}>
            Back
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCalculateNext}
            disabled={!cogs || cogs >= revenue}
          >
            Next
          </Button>
        </Box>
      </Box>
    );
  }

  // mode === 'know'
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        What is your gross margin?
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
        Gross margin = (Revenue - COGS) / Revenue × 100
      </Typography>
      <TextField
        fullWidth
        type="number"
        label="Gross Margin"
        value={responses.grossMargin || ''}
        onChange={(e) => {
          const val = parseFloat(e.target.value);
          if (!isNaN(val) && val >= 0 && val <= 100) {
            handleResponse('grossMargin', val);
          } else if (e.target.value === '') {
            handleResponse('grossMargin', '');
          }
        }}
        InputProps={{
          endAdornment: <span style={{ marginLeft: 4 }}>%</span>,
        }}
        inputProps={{ min: 0, max: 100, step: 0.1 }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={() => setMode('choose')} sx={{ color: '#fff' }}>
          Back
        </Button>
        <Button 
          variant="contained" 
          onClick={handleKnowNext}
          disabled={!responses.grossMargin}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

function QuestionStep({ 
  questions, 
  responses, 
  handleResponse,
  onEnterPress,
  timeframe
}: { 
  questions: FinancialQuestion[];
  responses: QuestionnaireResponse;
  handleResponse: (id: string, value: string | number) => void;
  onEnterPress: () => void;
  timeframe: 'monthly' | 'annual';
}) {
  const validateInput = (e: React.ChangeEvent<HTMLInputElement>, question: FinancialQuestion) => {
    const value = e.target.value;
    if (value === '') {
      handleResponse(question.id, '');
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    if (question.isPercent) {
      if (numValue >= 0 && numValue <= 100) {
        handleResponse(question.id, numValue);
      }
    } else {
      if (numValue >= 0) {
        handleResponse(question.id, numValue);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onEnterPress();
    }
  };

  const getLabel = (q: FinancialQuestion) => {
    if (q.isPercent) return 'Percentage';
    return `Amount (${timeframe === 'monthly' ? 'Monthly' : 'Annual'})`;
  };

  return (
    <Box sx={{ mt: 2 }}>
      {questions.map((q) => (
        <Box key={q.id} sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {q.question}
          </Typography>
          {q.helpText && (
            <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>
              {q.helpText}
            </Typography>
          )}
          <TextField
            fullWidth
            label={getLabel(q)}
            type="number"
            value={responses[q.id] || ''}
            onChange={(e) => validateInput(e as React.ChangeEvent<HTMLInputElement>, q)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: !q.isPercent ? <span style={{ marginRight: 4 }}>$</span> : null,
              endAdornment: q.isPercent ? <span style={{ marginLeft: 4 }}>%</span> : null,
            }}
            inputProps={{
              min: 0,
              max: q.isPercent ? 100 : undefined,
              step: q.isPercent ? 0.1 : 1
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

function PLPreview({ 
  data, 
  benchmarks,
  timeframe 
}: { 
  data: { label: string; value: string; indent?: boolean; bold?: boolean; highlight?: boolean }[];
  benchmarks: Benchmark[];
  timeframe: 'monthly' | 'annual';
}) {
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
        Your P&L Summary ({timeframe === 'monthly' ? 'Monthly' : 'Annual'})
      </Typography>
      
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableBody>
            {data.map((row, i) => (
              <TableRow key={i} sx={{ 
                backgroundColor: row.highlight ? 'rgba(255,255,255,0.1)' : 'transparent',
              }}>
                <TableCell sx={{ 
                  color: '#fff', 
                  pl: row.indent ? 4 : 2,
                  fontWeight: row.bold ? 700 : 400,
                  fontSize: row.bold ? '1.1rem' : '0.95rem',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}>
                  {row.label}
                </TableCell>
                <TableCell align="right" sx={{ 
                  color: '#fff',
                  fontWeight: row.bold ? 700 : 400,
                  fontSize: row.bold ? '1.1rem' : '0.95rem',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}>
                  {row.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        How You Compare
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {benchmarks.map((b, i) => (
          <Box key={i}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{b.label}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {b.value.toFixed(1)}{b.unit}
                </Typography>
                <Chip 
                  label={b.status === 'good' ? '✓ Good' : b.status === 'warning' ? '⚠ Watch' : '✗ Low'}
                  size="small"
                  sx={{ 
                    backgroundColor: b.status === 'good' ? '#22c55e' : b.status === 'warning' ? '#eab308' : '#ef4444',
                    color: '#fff',
                    fontSize: '0.7rem',
                    height: 20
                  }}
                />
              </Box>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(100, (b.value / b.benchmark) * 100)} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: b.status === 'good' ? '#22c55e' : b.status === 'warning' ? '#eab308' : '#ef4444',
                }
              }}
            />
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              Benchmark: {b.benchmark}{b.unit}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function App() {
  const [activeStep, setActiveStep] = useState(0);
  const [responses, setResponses] = useState<QuestionnaireResponse>({});
  const [showAddExpenseDialog, setShowAddExpenseDialog] = useState(false);
  const [customExpenses, setCustomExpenses] = useState<CustomExpense[]>([]);
  const [newExpenseTitle, setNewExpenseTitle] = useState('');
  const [newExpenseAmount, setNewExpenseAmount] = useState('');
  const [askingForMore, setAskingForMore] = useState(false);
  const [wantsToAddMore, setWantsToAddMore] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [timeframe, setTimeframe] = useState<'monthly' | 'annual'>('monthly');

  const handleNext = () => {
    // Step 0: Income - need revenue
    if (activeStep === 0 && !responses.revenue) {
      return;
    }
    // Step 1: Gross Margin - handled by GrossMarginStep component itself
    // Step 2 & 3: Expenses & Operations - optional fields
    
    if (activeStep === categories.length - 1) {
      setAskingForMore(true);
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleResponse = (id: string, value: string | number) => {
    setResponses((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleAddExpenseResponse = (response: string) => {
    setWantsToAddMore(response);
    if (response === 'no') {
      setShowPreview(true);
    } else {
      setShowAddExpenseDialog(true);
    }
  };

  const handleAddExpense = () => {
    if (newExpenseTitle && newExpenseAmount) {
      setCustomExpenses([
        ...customExpenses,
        {
          title: newExpenseTitle,
          amount: Number(newExpenseAmount)
        }
      ]);
      setNewExpenseTitle('');
      setNewExpenseAmount('');
      setShowAddExpenseDialog(false);
      setWantsToAddMore('');
      setAskingForMore(true);
    }
  };

  const calculatePL = () => {
    const multiplier = timeframe === 'annual' ? 12 : 1;
    const revenue = Number(responses.revenue) || 0;
    const grossMarginDecimal = responses.grossMargin ? Number(responses.grossMargin) / 100 : 0;
    const cogs = revenue * (1 - grossMarginDecimal);
    const grossProfit = revenue - cogs;
    
    const shippingDecimal = responses.shippingPercent ? Number(responses.shippingPercent) / 100 : 0;
    const shippingCosts = revenue * shippingDecimal;
    const creditCardFees = revenue * 0.03;

    const adSpend = (Number(responses.adSpend) || 0) * multiplier;
    const employeeSpend = (Number(responses.employeeSpend) || 0) * multiplier;
    const salesExpenses = (Number(responses.salesExpenses) || 0) * multiplier;
    const techExpenses = (Number(responses.techExpenses) || 0) * multiplier;
    const contractorExpenses = (Number(responses.contractorExpenses) || 0) * multiplier;
    const officeUtilities = (Number(responses.officeUtilities) || 0) * multiplier;
    const customTotal = customExpenses.reduce((sum, e) => sum + e.amount, 0) * multiplier;

    const totalExpenses = adSpend + employeeSpend + salesExpenses + techExpenses + 
      contractorExpenses + shippingCosts + creditCardFees + officeUtilities + customTotal;
    
    const netProfit = grossProfit - totalExpenses;
    const netMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
    const adSpendPercent = revenue > 0 ? (adSpend / revenue) * 100 : 0;
    const opexPercent = revenue > 0 ? (totalExpenses / revenue) * 100 : 0;

    return {
      revenue,
      cogs,
      grossProfit,
      grossMarginDecimal,
      adSpend,
      employeeSpend,
      salesExpenses,
      techExpenses,
      contractorExpenses,
      shippingCosts,
      creditCardFees,
      officeUtilities,
      customExpenses: customExpenses.map(e => ({ ...e, amount: e.amount * multiplier })),
      totalExpenses,
      netProfit,
      netMargin,
      adSpendPercent,
      opexPercent,
    };
  };

  const getPLData = () => {
    const pl = calculatePL();
    const fmt = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    
    return [
      { label: 'Revenue', value: fmt(pl.revenue), bold: true },
      { label: 'Cost of Goods Sold', value: `(${fmt(pl.cogs)})`, indent: true },
      { label: 'Gross Profit', value: fmt(pl.grossProfit), bold: true, highlight: true },
      { label: '', value: '' },
      { label: 'Operating Expenses', value: '', bold: true },
      { label: 'Advertising & Marketing', value: `(${fmt(pl.adSpend)})`, indent: true },
      { label: 'Payroll', value: `(${fmt(pl.employeeSpend)})`, indent: true },
      { label: 'Sales Expenses', value: `(${fmt(pl.salesExpenses)})`, indent: true },
      { label: 'Tech/Software', value: `(${fmt(pl.techExpenses)})`, indent: true },
      { label: 'Contractors', value: `(${fmt(pl.contractorExpenses)})`, indent: true },
      { label: 'Shipping/Fulfillment', value: `(${fmt(pl.shippingCosts)})`, indent: true },
      { label: 'Credit Card Fees (3%)', value: `(${fmt(pl.creditCardFees)})`, indent: true },
      { label: 'Office & Utilities', value: `(${fmt(pl.officeUtilities)})`, indent: true },
      ...pl.customExpenses.map(e => ({ label: e.title, value: `(${fmt(e.amount)})`, indent: true })),
      { label: 'Total Operating Expenses', value: `(${fmt(pl.totalExpenses)})`, bold: true },
      { label: '', value: '' },
      { label: 'Net Profit', value: fmt(pl.netProfit), bold: true, highlight: true },
      { label: 'Net Margin', value: `${pl.netMargin.toFixed(1)}%`, bold: true },
    ];
  };

  const getBenchmarks = (): Benchmark[] => {
    const pl = calculatePL();
    const grossMargin = Number(responses.grossMargin) || 0;
    
    const getStatus = (value: number, goodThreshold: number, isHigherBetter: boolean): 'good' | 'warning' | 'bad' => {
      if (isHigherBetter) {
        if (value >= goodThreshold) return 'good';
        if (value >= goodThreshold * 0.6) return 'warning';
        return 'bad';
      } else {
        if (value <= goodThreshold) return 'good';
        if (value <= goodThreshold * 1.5) return 'warning';
        return 'bad';
      }
    };

    return [
      {
        label: 'Gross Margin',
        value: grossMargin,
        benchmark: BENCHMARKS.grossMargin.good,
        unit: '%',
        status: getStatus(grossMargin, BENCHMARKS.grossMargin.good, true),
      },
      {
        label: 'Ad Spend (% of Revenue)',
        value: pl.adSpendPercent,
        benchmark: BENCHMARKS.adSpendPercent.good,
        unit: '%',
        status: getStatus(pl.adSpendPercent, BENCHMARKS.adSpendPercent.good, false),
      },
      {
        label: 'Net Margin',
        value: pl.netMargin,
        benchmark: BENCHMARKS.netMargin.good,
        unit: '%',
        status: getStatus(pl.netMargin, BENCHMARKS.netMargin.good, true),
      },
    ];
  };

  const generateExcel = () => {
    const pl = calculatePL();
    const fmt = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    
    const excelData = [
      ['P&L Statement', timeframe === 'monthly' ? 'Monthly' : 'Annual'],
      ['', ''],
      ['Revenue', fmt(pl.revenue)],
      ['Cost of Goods Sold', fmt(pl.cogs)],
      ['Gross Profit', fmt(pl.grossProfit)],
      [`Gross Margin`, `${(pl.grossMarginDecimal * 100).toFixed(1)}%`],
      ['', ''],
      ['Operating Expenses', ''],
      ['Advertising & Marketing', fmt(pl.adSpend)],
      ['Payroll', fmt(pl.employeeSpend)],
      ['Sales Expenses', fmt(pl.salesExpenses)],
      ['Tech/Software', fmt(pl.techExpenses)],
      ['Contractors', fmt(pl.contractorExpenses)],
      ['Shipping/Fulfillment', fmt(pl.shippingCosts)],
      ['Credit Card Fees (3%)', fmt(pl.creditCardFees)],
      ['Office & Utilities', fmt(pl.officeUtilities)],
      ...pl.customExpenses.map(e => [e.title, fmt(e.amount)]),
      ['Total Operating Expenses', fmt(pl.totalExpenses)],
      ['', ''],
      ['Net Profit', fmt(pl.netProfit)],
      ['Net Margin', `${pl.netMargin.toFixed(1)}%`],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    worksheet['!cols'] = [{ wch: 28 }, { wch: 18 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'P&L Statement');
    XLSX.writeFile(workbook, `pl-statement-${timeframe}.xlsx`);
  };

  const handleStartOver = () => {
    setActiveStep(0);
    setResponses({});
    setCustomExpenses([]);
    setShowPreview(false);
    setAskingForMore(false);
    setWantsToAddMore('');
  };

  const getCurrentQuestions = () => {
    return questions.filter((q) => q.category === categories[activeStep]);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="sm" sx={{ mb: 4, minHeight: '100vh', py: 4 }}>
        <Paper variant="outlined" sx={{ 
          my: { xs: 3, md: 6 }, 
          p: { xs: 2, md: 3 },
          backgroundColor: '#123b4f',
          color: '#ffffff',
          borderRadius: '25px',
          '& .MuiStepLabel-label': { color: '#ffffff !important' },
          '& .MuiStepIcon-root': { color: '#ffffff' },
          '& .MuiStepIcon-root.Mui-active': { color: '#ffffff' },
          '& .MuiStepIcon-root.Mui-completed': { color: '#ffffff' },
          '& .MuiStepLabel-label.Mui-active': { color: '#ffffff !important' },
          '& .MuiStepLabel-label.Mui-completed': { color: '#ffffff !important' },
          '& .MuiStepIcon-text': { fill: '#123b4f' },
          '& .MuiTextField-root': {
            '& .MuiInputBase-input': { color: '#ffffff' },
            '& .MuiInputLabel-root': { color: '#ffffff' },
            '& .MuiFormHelperText-root': { color: '#ffffff' },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#ffffff' },
              '&:hover fieldset': { borderColor: '#ffffff' },
              '&.Mui-focused fieldset': { borderColor: '#ffffff' }
            }
          },
          '& .MuiRadio-root': { color: '#ffffff' },
          '& .MuiFormControlLabel-label': { color: '#ffffff' },
        }}>
          <Typography component="h1" variant="h4" align="center" sx={{ mb: 2, color: '#ffffff' }}>
            Simple P&L Tool
          </Typography>
          
          {/* Timeframe Toggle */}
          {!showPreview && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <ToggleButtonGroup
                value={timeframe}
                exclusive
                onChange={(_, v) => v && setTimeframe(v)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                    }
                  }
                }}
              >
                <ToggleButton value="monthly">Monthly</ToggleButton>
                <ToggleButton value="annual">Annual</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {showPreview ? (
            <Box>
              <PLPreview 
                data={getPLData()} 
                benchmarks={getBenchmarks()}
                timeframe={timeframe}
              />
              
              <Box sx={{ display: 'flex', gap: 2, mt: 4, flexDirection: 'column' }}>
                <Button
                  variant="contained"
                  onClick={generateExcel}
                  fullWidth
                  sx={{ 
                    backgroundColor: '#22c55e',
                    '&:hover': { backgroundColor: '#16a34a' }
                  }}
                >
                  Download Excel
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleStartOver}
                  fullWidth
                  sx={{ 
                    borderColor: '#fff',
                    color: '#fff',
                    '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  Start Over
                </Button>
              </Box>

              {/* Pentane CTA */}
              <Box sx={{ 
                mt: 4, 
                p: 2, 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                borderRadius: 2,
                textAlign: 'center'
              }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Want to track this in real-time?
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                  Pentane gives you live P&L visibility, ad spend tracking, and profitability alerts — so you never fly blind again.
                </Typography>
                <Button
                  variant="contained"
                  href="https://pentane.com"
                  target="_blank"
                  sx={{ 
                    backgroundColor: '#fff',
                    color: '#123b4f',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: '#f0f0f0' }
                  }}
                >
                  Explore Pentane →
                </Button>
              </Box>
            </Box>
          ) : !askingForMore ? (
            <>
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {categories.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <Box>
                {activeStep === 1 ? (
                  // Gross Margin step with calculator option
                  <GrossMarginStep
                    responses={responses}
                    handleResponse={handleResponse}
                    onNext={handleNext}
                    timeframe={timeframe}
                  />
                ) : (
                  // Regular question steps
                  <>
                    <QuestionStep
                      questions={getCurrentQuestions()}
                      responses={responses}
                      handleResponse={handleResponse}
                      onEnterPress={handleNext}
                      timeframe={timeframe}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                      {activeStep !== 0 && (
                        <Button onClick={handleBack} sx={{ mr: 1, color: '#fff' }}>
                          Back
                        </Button>
                      )}
                      <Button variant="contained" onClick={handleNext}>
                        Next
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Any other expenses to add?
              </Typography>
              <RadioGroup
                value={wantsToAddMore}
                onChange={(e) => handleAddExpenseResponse(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes, add another" />
                <FormControlLabel value="no" control={<Radio />} label="No, show my P&L" />
              </RadioGroup>
            </Box>
          )}
        </Paper>

        <Dialog open={showAddExpenseDialog} onClose={() => setShowAddExpenseDialog(false)}>
          <DialogTitle>Add Custom Expense</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Expense Name"
              fullWidth
              value={newExpenseTitle}
              onChange={(e) => setNewExpenseTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label={`Amount (${timeframe === 'monthly' ? 'Monthly' : 'Annual'})`}
              type="number"
              fullWidth
              value={newExpenseAmount}
              onChange={(e) => setNewExpenseAmount(e.target.value)}
              InputProps={{
                startAdornment: <span style={{ marginRight: 4 }}>$</span>,
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddExpenseDialog(false)}>Cancel</Button>
            <Button onClick={handleAddExpense} variant="contained">
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
}

export default App;
